import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { cn } from '../../../lib/utils';
import { EASE_OUT } from '../../portal';

/** 12px uppercase seeker-400 label used above every value on the release page. */
export function DetailLabel({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'text-xs font-medium uppercase leading-4 tracking-[0.08em] text-seeker-400',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Card-footer disclosure ("FINGERPRINTS 2 ⌄"). Defaults to the footer of a
 * `p-6` card; override the wrapper/body classes to place it elsewhere. The
 * body animates its height open and closed (180ms) instead of snapping.
 */
export function DetailDisclosure({
  label,
  count,
  trailing,
  className,
  bodyClassName,
  children,
}: {
  label: ReactNode;
  count?: number;
  trailing?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  return (
    <div
      className={cn('-mx-6 -mb-6 mt-6 border-t border-seeker-800', className)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center justify-between gap-4 px-6 py-4 text-left text-seeker-400 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70"
      >
        <span className="flex flex-wrap items-center gap-3">
          <DetailLabel className="text-current">{label}</DetailLabel>
          {typeof count === 'number' && (
            <span className="text-xs tabular-nums text-seeker-500">
              {count}
            </span>
          )}
          {trailing}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'size-4 shrink-0 transition-transform duration-150 ease-out',
            open && 'rotate-180',
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="body"
            id={bodyId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className={cn('px-6 pb-6', bodyClassName)}>{children}</div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
